// This object will hold the context provided by our React app.
// It is private to this module.
let _context = {
  sessionId: 'NO_SESSION_INITIAL',
  userId: 'NO_USER',
  userDisplayName: 'NO_USER',
};

/**
 * This function is called from React to update the logger's context.
 * @param {object} newContext An object with sessionId, userId, etc.
 */
export function updateLoggerContext(newContext) {
  _context = { ..._context, ...newContext };
}

/**
 * Sends a log message to the backend server.
 * @param {string} level The log level (e.g., 'info', 'warn', 'error').
 * @param {string} message The primary log message.
 * @param {object} [details={}] Additional structured details.
 */

async function logToServer(level, message, details = {}) {
	const logEndpoint = '/api/log'; // Relative path to our Cloud Run logging endpoint

	try {
        // GATHER CONTEXT for the formatted string ---
        const { sessionId, userId, userDisplayName } = _context; // <-- Reads from the stored context
        
        
        const api = details.api || 'N/A';
        const source = details.functionName ? `[CLIENT:${details.functionName}]` : '[CLIENT]';

        let formattedMessage = `${source} SessionID = ${sessionId} ; UserID = ${userId} ; API = ${api} ; message = ${message}`;
        
		// Automatically enrich logs with Office.js context if available
		const officeContext = Office.context ? {
			platform: Office.context.platform,
			host: Office.context.host,
			version: Office.context.diagnostics ? Office.context.diagnostics.version : 'unknown',
			sessionId: sessionStorage.getItem('sessionId') // Include session ID for tracing
		} : {
			platform: 'N/A',
			host: 'N/A'
		};
        // ---Automatically add user details to every log ---
        const enrichedDetails = { ...details };
        if (userDisplayName) {
            enrichedDetails.userName = userDisplayName;
        }
        if (userId) {
            enrichedDetails.userId = userId;
        }
        
		// Use fetch to send the log data. 'keepalive' is useful for logs sent during page unload.
		fetch(logEndpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				level: level,
				message: formattedMessage,
				context: officeContext,
				...enrichedDetails,
			}),
			keepalive: true,
		}).catch(error => {
			// This inner catch handles failures of the fetch call itself (e.g., network down)
			console.error('CRITICAL: Fetch to logging endpoint failed.', { originalLog: { level, message }, loggingError: error });
		});

	} catch (error) {
		// This outer catch is a safeguard for any synchronous error before the fetch call.
		console.error('CRITICAL: Synchronous error in logToServer function.', { originalLog: { level, message }, loggingError: error });
	}
}


// The logger object remains the same, but your error handler is preserved.
const logger = {
  info: (message, details) => logToServer('info', message, details),
  warn: (message, details) => logToServer('warn', message, details),
  error: (message, details) => {
    const logPayload = { ...details };
    let errorObject = null;
    if (details instanceof Error) {
      errorObject = details;
    } else if (details && details.originalError instanceof Error) {
      errorObject = details.originalError;
    } else if (details && details.error instanceof Error) {
      errorObject = details.error;
    }
    if (errorObject) {
      if (!logPayload.errorMessage) {
        logPayload.errorMessage = errorObject.message;
      }
      logPayload.stack = errorObject.stack;
      delete logPayload.originalError;
      delete logPayload.error;
    }
    logToServer('error', message, logPayload);
  },
};

export default logger;